import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Trophy, Bicycle, PersonSimpleRun, Racquet, Crown, LockSimple } from "@phosphor-icons/react";

const SPORTS = [
  { key: "all", icon: Trophy },
  { key: "cycling", icon: Bicycle },
  { key: "running", icon: PersonSimpleRun },
  { key: "padel", icon: Racquet },
];
const PERIODS = ["week", "month", "all_time"];

export default function Leaderboard() {
  const { lang } = useI18n();
  const { user } = useAuth();
  const [sport, setSport] = useState("all");
  const [period, setPeriod] = useState("week");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/leaderboard?sport=${sport}&period=${period}&limit=50`)
      .then((r) => { if (alive) { setData(r.data); setLoading(false); } })
      .catch(() => { if (alive) { setData({ leaderboard: [], me: null, primary_metric: "total_km" }); setLoading(false); } });
    return () => { alive = false; };
  }, [sport, period]);

  const tSport = (k) => {
    if (lang === "da") return { all: "Alle", cycling: "Cykling", running: "Løb", padel: "Padel" }[k];
    return { all: "All", cycling: "Cycling", running: "Running", padel: "Padel" }[k];
  };
  const tPeriod = (k) => {
    if (lang === "da") return { week: "Denne uge", month: "Denne måned", all_time: "Hele tiden" }[k];
    return { week: "This week", month: "This month", all_time: "All time" }[k];
  };

  const primary = data?.primary_metric || "total_km";
  const primaryLabel = useMemo(() => {
    if (primary === "total_km") return lang === "da" ? "KM" : "KM";
    return lang === "da" ? "SESSIONER" : "SESSIONS";
  }, [primary, lang]);

  // If the user isn't in the top 50, surface a sticky "your rank" row.
  const meInTop = data?.leaderboard?.some((r) => r.is_me);
  const showStickyMe = data?.me && !meInTop;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <AppHeader />
      <main className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="label-overline flex items-center gap-2" style={{ color: "#FFCC00" }}>
              <Crown weight="fill" size={12} color="#FFCC00" />
              {lang === "da" ? "Rangliste · Pro" : "Leaderboard · Pro"}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-2">
              {lang === "da" ? "HVEM HAR FLEST KM I UGEN?" : "WHO RACKED UP THE MOST THIS WEEK?"}
            </h1>
            <p className="text-zinc-400 mt-3 max-w-xl text-sm">
              {lang === "da"
                ? "Rangering baseret på gennemførte sessioner. Vis dig frem, eller find nogen at jagte."
                : "Ranked by completed sessions. Show up — or find someone to chase."}
            </p>
          </div>
          {data?.window_start && (
            <div className="text-right">
              <div className="label-overline">{lang === "da" ? "Periode starter" : "Period starts"}</div>
              <div className="font-mono-tag text-sm text-[#FFCC00] mt-1">
                {new Date(data.window_start).toLocaleDateString(lang === "da" ? "da-DK" : "en-US", { day: "2-digit", month: "short" })}
              </div>
            </div>
          )}
        </div>

        {/* Sport tabs */}
        <div className="mt-8 flex flex-wrap gap-2" role="tablist" aria-label="sport">
          {SPORTS.map((s) => {
            const Icon = s.icon;
            const active = sport === s.key;
            return (
              <button
                key={s.key}
                role="tab"
                aria-selected={active}
                onClick={() => setSport(s.key)}
                data-testid={`leaderboard-sport-${s.key}`}
                className={`kinetic-btn flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-widest font-bold border ${
                  active ? "bg-[#FFCC00] text-black border-[#FFCC00]" : "bg-[#0A0A0A] text-zinc-300 border-[#262626] hover:border-white/40"
                }`}
              >
                <Icon weight="bold" size={14} />
                {tSport(s.key)}
              </button>
            );
          })}
        </div>

        {/* Period toggle */}
        <div className="mt-3 flex flex-wrap gap-2" role="tablist" aria-label="period">
          {PERIODS.map((p) => {
            const active = period === p;
            return (
              <button
                key={p}
                role="tab"
                aria-selected={active}
                onClick={() => setPeriod(p)}
                data-testid={`leaderboard-period-${p}`}
                className={`text-xs uppercase tracking-widest font-bold px-3 py-2 border ${
                  active ? "bg-white text-black border-white" : "bg-transparent text-zinc-400 border-[#262626] hover:border-white/40"
                }`}
              >
                {tPeriod(p)}
              </button>
            );
          })}
        </div>

        {/* Table */}
        <section
          className="mt-8 border border-[#262626] bg-[#141414] divide-y divide-[#262626]"
          data-testid="leaderboard-table"
        >
          <header className="grid grid-cols-[3.5rem_1fr_auto_auto] gap-4 items-center px-5 py-3 text-zinc-500 label-overline">
            <span>{lang === "da" ? "PLAC" : "RANK"}</span>
            <span>{lang === "da" ? "ATLET" : "ATHLETE"}</span>
            <span className="text-right">{primary === "total_km" ? primaryLabel : (lang === "da" ? "SESS." : "SESS.")}</span>
            <span className="text-right">{primary === "total_km" ? (lang === "da" ? "SESS." : "SESS.") : "KM"}</span>
          </header>

          {loading ? (
            <div className="px-5 py-10 text-center text-zinc-500 text-sm" data-testid="leaderboard-loading">
              {lang === "da" ? "Indlæser…" : "Loading…"}
            </div>
          ) : (data?.leaderboard?.length || 0) === 0 ? (
            <EmptyState lang={lang} />
          ) : (
            data.leaderboard.map((row) => (
              <Row
                key={row.user_id}
                row={row}
                primary={primary}
                currentUserId={user?.user_id}
              />
            ))
          )}
        </section>

        {/* Sticky "you're #N" row */}
        {showStickyMe && (
          <section
            className="mt-3 border border-[#FFCC00]/50 bg-[#FFCC00]/[0.06] divide-y divide-[#FFCC00]/20"
            data-testid="leaderboard-me-sticky"
          >
            <Row row={data.me} primary={primary} currentUserId={user?.user_id} highlight />
          </section>
        )}

        {data?.me === null && !loading && (
          <div
            className="mt-4 border border-[#262626] bg-[#141414] p-5 text-sm text-zinc-400"
            data-testid="leaderboard-empty-me"
          >
            {lang === "da"
              ? "Du har ingen gennemførte sessioner i denne periode endnu. Tilmeld dig en — eller vært én — for at komme på listen."
              : "You haven't completed any sessions in this period yet. Join one — or host one — to land on the board."}{" "}
            <Link to="/explore" className="text-[#FFCC00] hover:underline font-bold uppercase tracking-widest text-xs ml-2" data-testid="leaderboard-explore-link">
              {lang === "da" ? "Find en session →" : "Find a session →"}
            </Link>
          </div>
        )}
      </main>
    </div>
  );
}

function Row({ row, primary, currentUserId, highlight = false }) {
  const isMe = row.is_me || row.user_id === currentUserId;
  const podium = row.rank <= 3 && !highlight;
  const podiumColor = ["#FFD700", "#C0C0C0", "#CD7F32"][row.rank - 1] || "#FFCC00";
  const initials = (row.name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div
      className={`grid grid-cols-[3.5rem_1fr_auto_auto] gap-4 items-center px-5 py-3 ${
        isMe ? "bg-[#FFCC00]/[0.08]" : ""
      }`}
      data-testid={`leaderboard-row-${row.rank}`}
    >
      <span className="font-display text-2xl tracking-tight" style={{ color: podium ? podiumColor : undefined }}>
        {row.rank <= 9 ? `0${row.rank}` : row.rank}
      </span>
      <div className="flex items-center gap-3 min-w-0">
        {row.picture ? (
          <img
            src={row.picture.startsWith("http") ? row.picture : `${process.env.REACT_APP_BACKEND_URL}${row.picture}`}
            alt=""
            className="w-9 h-9 object-cover border border-[#262626]"
          />
        ) : (
          <div className="w-9 h-9 bg-[#262626] flex items-center justify-center text-xs font-bold">
            {row.is_private ? <LockSimple weight="bold" size={14} /> : initials}
          </div>
        )}
        <div className="min-w-0">
          <div className="text-sm font-semibold truncate flex items-center gap-1.5" data-testid={`leaderboard-name-${row.rank}`}>
            {row.name}
            {row.is_pro && <Crown weight="fill" size={12} color="#FFCC00" />}
            {isMe && (
              <span className="ml-1 text-[9px] uppercase tracking-widest font-bold text-[#FFCC00] bg-[#FFCC00]/15 px-1.5 py-0.5">
                YOU
              </span>
            )}
          </div>
          <div className="text-[11px] text-zinc-500 uppercase tracking-widest font-bold">
            {(row.sports || []).join(" · ")}
          </div>
        </div>
      </div>
      <span className="font-mono-tag text-right text-base" data-testid={`leaderboard-primary-${row.rank}`}>
        {primary === "total_km" ? row.total_km.toFixed(1) : row.total_sessions}
      </span>
      <span className="font-mono-tag text-right text-sm text-zinc-400">
        {primary === "total_km" ? row.total_sessions : (row.total_km ? row.total_km.toFixed(1) : "—")}
      </span>
    </div>
  );
}

function EmptyState({ lang }) {
  return (
    <div className="px-5 py-12 text-center text-zinc-500 text-sm" data-testid="leaderboard-empty">
      <Trophy weight="duotone" size={32} className="mx-auto mb-3 text-zinc-700" />
      <div>{lang === "da"
        ? "Ingen gennemførte sessioner i denne periode endnu."
        : "No completed sessions in this period yet."}</div>
      <div className="mt-1 text-xs">
        {lang === "da"
          ? "Vær den første — og indtag toppen."
          : "Be the first — claim the top spot."}
      </div>
    </div>
  );
}
